import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	faArrowUpRightFromSquare,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon, OutputLanguageSelect } from "@/components";
import { TextArea } from "@/components/TextArea/TextArea";
import { ROUTE_CONFIG } from "@/routes/config";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useUpdateScoreMutation } from "@/pages/Private/redux/score/score.api";

import { ScoreLeaveGuard } from "../../shell/ScoreLeaveGuard";
import { ScoreSaveBar } from "../../shell/ScoreSaveBar";
import { ScoreShell } from "../../shell/ScoreShell";
import { SettingsBox } from "../../shell/SettingsBox";
import { useScoreConfig } from "../../shell/useScoreConfig";

import {
	applyGeneralForm,
	GeneralFormSchema,
	GeneralFormValues,
	isGeneralDirty,
	toGeneralForm,
} from "./generalForm";

const HELP_URL = "https://help.signalist.io/lead-scoring";

const EMPTY_FORM: GeneralFormValues = {
	serviceDescription: "",
	competitorDescription: "",
	disqualifyCompaniesText: "",
	language: null,
	configuration: {},
	personaConfiguration: {},
};

const Hint: FunctionComponent<{ text: string }> = ({ text }) => (
	<div className="mt-2 flex items-start gap-2 rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark">
		<Icon className="mt-0.5 h-3 w-3 shrink-0" icon={faTriangleExclamation} />
		<span>{text}</span>
	</div>
);

const LinkButton: FunctionComponent<{ to: string; label: string }> = ({ to, label }) => (
	<Link
		className="flex h-6 cursor-pointer items-center gap-1.5 rounded-md border border-border-light bg-white px-2 text-[11px] font-medium text-text hover:bg-background-light"
		to={to}
	>
		<Icon className="h-3 w-3" icon={faArrowUpRightFromSquare} />
		{label}
	</Link>
);

/**
 * General settings (PREC-4055): the company-wide fields that describe the
 * user's own company rather than one segment — what it does, who competes with
 * it, which companies are disqualified outright, and the language the AI
 * writes in.
 *
 * The structured company rules and the "Disqualify people" filters were dropped
 * here (PREC-4079): the page is the free-text brief the AI reads, and the
 * mockup has only these four boxes. Whatever is stored in `configuration` and
 * `personaConfiguration` is still loaded into the form and written back
 * untouched on save, so no rule a customer already set is lost.
 */
export const ScoreGeneral: FunctionComponent = () => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);
	const { score, refetch } = useScoreConfig();
	const [updateScore] = useUpdateScoreMutation();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Keyed on content, not identity: the score query refetches on mount and hands the slice a
	// new object with the same values, and a reset on that would wipe the user's first edit.
	const savedKey = score ? JSON.stringify(toGeneralForm(score)) : "";
	const saved = useMemo<GeneralFormValues>(
		() => (savedKey ? (JSON.parse(savedKey) as GeneralFormValues) : EMPTY_FORM),
		[savedKey]
	);

	const { watch, setValue, reset, getValues } = useForm<GeneralFormValues>({
		defaultValues: saved,
		resolver: zodResolver(GeneralFormSchema),
	});

	useEffect(() => {
		reset(saved);
	}, [saved, reset]);

	const values = watch();
	const dirty = isGeneralDirty(values, saved);

	const save = async (): Promise<boolean> => {
		if (!score) {
			return false;
		}

		setSaving(true);
		setError(null);

		try {
			await updateScore(applyGeneralForm(score, getValues())).unwrap();
			await refetch();

			return true;
		} catch {
			setError(t("leadScoring.saveError"));

			return false;
		} finally {
			setSaving(false);
		}
	};

	const discard = () => {
		reset(saved);
		setError(null);
	};

	const textArea = (
		name: "serviceDescription" | "competitorDescription" | "disqualifyCompaniesText",
		placeholder: string
	) => (
		<TextArea
			className="min-h-[96px]"
			handleChange={(event) => setValue(name, event.target.value)}
			name={name}
			placeholder={placeholder}
			showError={false}
			value={values[name]}
		/>
	);

	return (
		<ScoreShell helpUrl={HELP_URL} intro={t("leadScoring.stepper.general")} step="general">
			<div className="max-w-[1000px]">
				<SettingsBox
					testId="general-about"
					title={t("leadScoring.general.about", { company: user?.company?.name ?? "" })}
				>
					{textArea("serviceDescription", t("leadScoring.general.aboutPlaceholder"))}
				</SettingsBox>

				<SettingsBox
					actions={
						<LinkButton
							label={t("leadScoring.general.openCompetitorList")}
							to={ROUTE_CONFIG.DO_NOT_CONTACT_COMPETITORS}
						/>
					}
					testId="general-competitors"
					title={t("leadScoring.general.competitors")}
				>
					{textArea("competitorDescription", t("leadScoring.general.competitorsPlaceholder"))}
					<Hint text={t("leadScoring.general.competitorsHint")} />
				</SettingsBox>

				<SettingsBox
					actions={
						<LinkButton
							label={t("leadScoring.general.openBlacklist")}
							to={ROUTE_CONFIG.DO_NOT_CONTACT_BLACKLIST}
						/>
					}
					testId="general-disqualify-companies"
					title={t("leadScoring.general.disqualifyCompanies")}
				>
					{textArea("disqualifyCompaniesText", t("leadScoring.disqualifyAllCompaniesPurely"))}
					<Hint text={t("leadScoring.general.disqualifyCompaniesHint")} />
				</SettingsBox>

				<SettingsBox testId="general-language" title={t("leadScoring.outputLanguage")}>
					<p className="mb-2 text-xs text-text-meta">{t("leadScoring.general.languageHint")}</p>
					<div className="w-[240px]">
						<OutputLanguageSelect
							disabled={saving}
							handleSelect={(language) => setValue("language", language)}
							label=""
							value={values.language}
						/>
					</div>
				</SettingsBox>
			</div>

			<ScoreSaveBar dirty={dirty} error={error} saving={saving} onDiscard={discard} onSave={save} />
			<ScoreLeaveGuard when={dirty} onDiscard={discard} onSave={save} />
		</ScoreShell>
	);
};
